import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const AdvancedOptions = ({ 
  options = {}, 
  onOptionsChange, 
  disabled = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [focusAreas, setFocusAreas] = useState(options?.focusAreas || []);
  const [analysisDepth, setAnalysisDepth] = useState(options?.analysisDepth || 'standard');
  const [budgetRange, setBudgetRange] = useState(options?.budgetRange || 'moderate');
  const [priorityType, setPriorityType] = useState(options?.priorityType || 'performance');

  const focusAreaOptions = [
    { value: 'drivetrain', label: 'Drivetrain Components', description: 'Chain, cassette, derailleurs, shifters' },
    { value: 'wheels', label: 'Wheels & Tires', description: 'Rims, hubs, spokes, tires, tubes' },
    { value: 'brakes', label: 'Braking System', description: 'Brake calipers, rotors, pads, cables' },
    { value: 'cockpit', label: 'Cockpit Setup', description: 'Handlebars, stem, grips, controls' },
    { value: 'saddle', label: 'Saddle & Seatpost', description: 'Seat, seatpost, clamp' },
    { value: 'frame', label: 'Frame & Fork', description: 'Frame condition, fork, headset' },
    { value: 'accessories', label: 'Accessories', description: 'Lights, computer mounts, bottles' }
  ];

  const analysisDepthOptions = [
    { value: 'quick', label: 'Quick Analysis', description: 'Basic component identification and obvious upgrades' },
    { value: 'standard', label: 'Standard Analysis', description: 'Comprehensive component review and recommendations' },
    { value: 'detailed', label: 'Detailed Analysis', description: 'In-depth analysis with compatibility checks and alternatives' }
  ];

  const budgetRangeOptions = [
    { value: 'budget', label: 'Budget Conscious ($0-$200)', description: 'Focus on essential, cost-effective upgrades' },
    { value: 'moderate', label: 'Moderate Budget ($200-$800)', description: 'Balance of performance and value' },
    { value: 'premium', label: 'Premium Budget ($800+)', description: 'High-end components and latest technology' },
    { value: 'unlimited', label: 'No Budget Limit', description: 'Show all available upgrade options' }
  ];

  const priorityTypeOptions = [
    { value: 'performance', label: 'Performance', description: 'Speed, efficiency, and competitive advantages' },
    { value: 'comfort', label: 'Comfort', description: 'Ergonomics, vibration damping, ease of use' },
    { value: 'durability', label: 'Durability', description: 'Long-lasting, reliable components' },
    { value: 'weight', label: 'Weight Reduction', description: 'Lighter components for better climbing' },
    { value: 'aesthetics', label: 'Aesthetics', description: 'Visual appeal and bike appearance' }
  ];

  const handleFocusAreaChange = (areaValue, checked) => {
    const newFocusAreas = checked 
      ? [...focusAreas, areaValue]
      : focusAreas?.filter(area => area !== areaValue);
    
    setFocusAreas(newFocusAreas);
    updateOptions({ focusAreas: newFocusAreas });
  };

  const updateOptions = (newOptions) => {
    const updatedOptions = {
      focusAreas,
      analysisDepth,
      budgetRange,
      priorityType,
      ...newOptions
    };
    
    if (onOptionsChange) {
      onOptionsChange(updatedOptions);
    }
  };

  const handleAnalysisDepthChange = (value) => {
    setAnalysisDepth(value);
    updateOptions({ analysisDepth: value });
  };

  const handleBudgetRangeChange = (value) => {
    setBudgetRange(value);
    updateOptions({ budgetRange: value });
  };

  const handlePriorityTypeChange = (value) => {
    setPriorityType(value);
    updateOptions({ priorityType: value });
  };

  return (
    <div className="w-full bg-card border border-border rounded-lg">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={disabled}
        className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/50'
        }`}
      >
        <div className="flex items-center space-x-3">
          <Icon name="Settings" size={20} color="var(--color-accent)" />
          <div>
            <h3 className="font-semibold text-foreground">Advanced Analysis Options</h3>
            <p className="text-sm text-muted-foreground">
              Customize your bike analysis preferences
            </p>
          </div>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={20} 
          color="var(--color-muted-foreground)" 
        />
      </button>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border p-4 space-y-6">
          {/* Analysis Depth */}
          <div>
            <Select
              label="Analysis Depth"
              description="Choose how comprehensive you want the analysis to be"
              options={analysisDepthOptions?.map(option => ({
                value: option?.value,
                label: option?.label,
                description: option?.description
              }))}
              value={analysisDepth}
              onChange={handleAnalysisDepthChange}
              disabled={disabled}
            />
          </div>

          {/* Budget Range */}
          <div>
            <Select
              label="Budget Range"
              description="Set your upgrade budget to get appropriate recommendations"
              options={budgetRangeOptions?.map(option => ({
                value: option?.value,
                label: option?.label,
                description: option?.description
              }))}
              value={budgetRange}
              onChange={handleBudgetRangeChange}
              disabled={disabled}
            />
          </div>

          {/* Priority Type */}
          <div>
            <Select
              label="Upgrade Priority"
              description="What's most important to you in upgrades?"
              options={priorityTypeOptions?.map(option => ({
                value: option?.value,
                label: option?.label,
                description: option?.description
              }))}
              value={priorityType}
              onChange={handlePriorityTypeChange}
              disabled={disabled}
            />
          </div>

          {/* Focus Areas */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Focus Areas (Optional)
            </label>
            <p className="text-sm text-muted-foreground mb-4">
              Select specific areas you want us to pay extra attention to during analysis.
            </p>
            
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {focusAreaOptions?.map((area) => (
                <div key={area?.value} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  <Checkbox
                    checked={focusAreas?.includes(area?.value)}
                    onChange={(e) => handleFocusAreaChange(area?.value, e?.target?.checked)}
                    disabled={disabled}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-foreground cursor-pointer">
                      {area?.label}
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {area?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="pt-4 border-t border-border">
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-accent font-medium mb-1">Analysis Summary</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• {analysisDepthOptions?.find(o => o?.value === analysisDepth)?.label}</li>
                    <li>• {budgetRangeOptions?.find(o => o?.value === budgetRange)?.label}</li>
                    <li>• Priority: {priorityTypeOptions?.find(o => o?.value === priorityType)?.label}</li>
                    {focusAreas?.length > 0 && (
                      <li>• Focus areas: {focusAreas?.length} selected</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedOptions;